import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swords, Clock, BrainCircuit, Award, TrendingUp, ArrowRight } from "lucide-react";
import { MOCK_BOSS_BATTLES } from "@/data/marketplaceMockData";

const DIFFICULTY_COLOR = { Medium: "#F59E0B", Hard: "#FB7185", Extreme: "#B58BFF" };

export const MultiDomainBossBattles = () => {
  return (
    <section id="boss-battles" className="relative max-w-7xl mx-auto px-6 py-24" data-testid="multi-domain-boss-battles-section">
      <div className="absolute inset-x-0 h-[500px] -z-10 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 50% 0%, #B58BFF18, transparent 60%)" }} />
      <div className="max-w-2xl mb-14">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#B58BFF] flex items-center gap-2">
          <Swords className="w-3.5 h-3.5" /> Boss Battles
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">
          Complete real-world projects instead of multiple-choice quizzes.
        </h2>
        <p className="text-slate-400 mt-3 max-w-lg">
          Every skill track ends its milestone weeks with an AI-evaluated Boss Battle — a real project or scenario, not a quiz. Clear it to earn a certificate and a serious leaderboard boost.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="boss-battle-card-grid">
        {MOCK_BOSS_BATTLES.map((b, i) => (
          <motion.div
            key={b.id} data-testid={`boss-battle-card-${b.id}`}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
            whileHover={{ y: -4 }}
            className="relative rounded-2xl border p-5 transition-all duration-300"
            style={{ borderColor: `${b.accent}30`, background: `linear-gradient(180deg, ${b.accent}10, transparent)` }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${b.accent}18`, border: `1px solid ${b.accent}30` }}>
                <Swords className="w-5 h-5" style={{ color: b.accent }} strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: `${DIFFICULTY_COLOR[b.difficulty] || b.accent}18`, color: DIFFICULTY_COLOR[b.difficulty] || b.accent }}>{b.difficulty}</span>
            </div>
            <p className="font-heading text-base font-semibold text-white leading-snug">{b.title}</p>

            <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
              <span className="flex items-center gap-1.5 text-amber-300 font-semibold">+{b.xp} XP</span>
              <span className="flex items-center gap-1.5 text-slate-400"><Clock className="w-3 h-3" /> {b.estimatedTime}</span>
              <span className="flex items-center gap-1.5 text-slate-400"><BrainCircuit className="w-3 h-3" /> AI Evaluation</span>
              <span className="flex items-center gap-1.5 text-slate-400"><Award className="w-3 h-3" /> Certificate</span>
            </div>
            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/5 text-[11px] font-semibold" style={{ color: b.accent }}>
              <TrendingUp className="w-3 h-3" /> {b.leaderboardImpact}
            </div>
          </motion.div>
        ))}
      </div>

      <Link
        to="/register" data-testid="multi-domain-boss-battles-cta"
        className="inline-flex items-center gap-2 mt-10 text-sm font-semibold text-white bg-violet-500 hover:bg-violet-400 px-6 py-3 rounded-xl transition-all hover:-translate-y-0.5"
      >
        Take On Your First Boss <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
};
